import { Box, Flex, Grid, GridItem, Image, Input, Select, Text, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, FormControl, FormLabel, Button, Center, Spinner, Tooltip, } from "@chakra-ui/react";
import MyDiv from "./announcement.style";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { AlertCard, CustomButton, CustomToast } from "../../../../components";
import { useEffect, useState } from "react";
import PlusIcon from "../../../../assets/images/Vector.svg";
import { useFetch } from "../../../../hooks/useFetch";
import { IAnnouncement } from "./model";
import { MultiSelectMenu } from "../../../../components/MultiSelectMenu/MultiSelectMenu";
import CustomTables, { Column, ILazyParams } from "../../../../components/CustomTable/CustomTables";
import { EditIcon } from "@chakra-ui/icons";
import { IconButton } from "@chakra-ui/react";

export interface ICompanyDropdown {
  company_ID: number;
  company_Name: string;
}

export interface IDepartment {
  id: number;
  departmentName: string;
}

interface IBranch {
  branchId: number;
  branchName: string;
}

const Announcement = () => {
  const [announcement, setAnnouncement] = useState<IAnnouncement[]>([]);
  const [AlertOpen, setAlertOpen] = useState(false);
  const [DrawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pendingStatus, setPendingStatus] = useState<boolean | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
  const [departments, setDepartments] = useState<IDepartment[]>([]);
  const [companies, setCompanies] = useState<ICompanyDropdown[]>([]);
  const [branchOptions, setBranchOptions] = useState<IBranch[]>([]);
  const [currentAnnouncement, setCurrentAnnouncement] = useState({ id: 0, topic: '', description: '', duration: 0, status: true, });
  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);
  const [statusChangeSource, setStatusChangeSource] = useState<"table" | "drawer" | null>(null);

  const [dataLoading, setDataLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [lazyParams, setLazyParams] = useState<ILazyParams>({
    first: 0, rows: 20, sortField: undefined, sortOrder: undefined,
    multiSortMeta: [], filters: {}, globalFilter: '',
  });

  useEffect(() => {
    loadAnnouncement();
  }, [lazyParams]);

  useEffect(() => {
    loadCompanies();
    loadDepartments();
  }, []);

  useEffect(() => {
    loadBranches(selectedCompanies);
    setSelectedBranches([]);
  }, [selectedCompanies]);

  const loadDepartments = async () => {
    const response = await fetchApi("Dropdown/department", "GET", null, null, "");
    if (response) {
      const updated = [{ id: 0, departmentName: "All Department" }, ...response];
      setDepartments(updated);
    }
  };

  const loadCompanies = async () => {
    const response = await fetchApi("Dropdown/company", "GET", null, null);
    if (response) {
      const updated = [{ company_ID: 0, company_Name: "All Company" }, ...response];
      setCompanies(updated);
    }
  };

  const loadBranches = async (companyIds: number[], preSelectedBranchIds?: number[]) => {
    if (!companyIds.length) {
      setBranchOptions([]);
      return;
    }

    const query = companyIds.map((id) => `companyIds=${id}`).join("&");
    const response = await fetchApi(`Dropdown/MultiBranchDropDown?${query}`, "GET", null, null);

    if (response) {
      const updated = [{ branchId: 0, branchName: "All Branch" }, ...response];
      setBranchOptions(updated);
      if (preSelectedBranchIds) {
        setSelectedBranches(preSelectedBranchIds);
      }
    }
  };

  const loadAnnouncement = async () => {
    setDataLoading(true);
    const params = new URLSearchParams();
    params.set('first', String(lazyParams.first));
    params.set('rows', String(lazyParams.rows));
    if (lazyParams.sortField) params.set('sortField', lazyParams.sortField);
    if (lazyParams.sortOrder) params.set('sortOrder', String(lazyParams.sortOrder));
    if (lazyParams.globalFilter) params.set('globalFilter', lazyParams.globalFilter);
    if (lazyParams.filters && Object.keys(lazyParams.filters).length) {
      params.set('filters', JSON.stringify(lazyParams.filters));
    }

    const response = await fetchApi<any>(`Hrlense_Score/GetAnnouncementsMaster?${params.toString()}`, "GET");
    if (response) {
      setAnnouncement(response.data ?? response ?? []);
      setTotalCount(response.totalRecords ?? response.length ?? 0);
    }
    setDataLoading(false);
  };

  const handleStatusChange = (id: number, newStatus: boolean) => {
    setSelectedId(id);
    setPendingStatus(newStatus);
    setStatusChangeSource("table");
    setAlertOpen(true);
  };

  const handleDrawerStatusChange = (newStatus: boolean) => {
    setPendingStatus(newStatus);
    setStatusChangeSource("drawer");
    setAlertOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (selectedId !== null && pendingStatus !== null) {
      const announcementId = statusChangeSource === "table" ? selectedId : currentAnnouncement.id;
      const response = await fetchApi(`Hrlense_Score/changestatusforAnnouncement/${announcementId}`, "PUT", { Id: announcementId, Status: pendingStatus });
      if (response) {
        if (statusChangeSource === "table") {
          await loadAnnouncement();
        } else {
          setCurrentAnnouncement((prev) => ({ ...prev, status: pendingStatus }));
        }
      }
    }
    setAlertOpen(false);
  };

  const handleAddNewAnnouncement = () => {
    setIsEditMode(false);
    setCurrentAnnouncement({ id: 0, topic: '', description: '', duration: 0, status: true });
    setSelectedCompanies([]);
    setSelectedBranches([]);
    setSelectedDepartments([]);
    setDrawerOpen(true);
  };

  const handleEditAnnouncement = (announcement: IAnnouncement) => {
    setIsEditMode(true);
    setSelectedId(announcement.id);
    setCurrentAnnouncement(announcement);
    setSelectedCompanies(announcement.companyIds || []);
    setSelectedDepartments(announcement.departmentIds || []);
    loadBranches(announcement.companyIds || [], announcement.branchIds || []);
    setDrawerOpen(true);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setCurrentAnnouncement((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!currentAnnouncement.topic.trim()) {
      addToast({ message: "Please enter Topic", status: "warning" });
      return;
    }
    if (!currentAnnouncement.description.trim()) {
      addToast({ message: "Please enter Description", status: "warning" });
      return;
    }
    if (!currentAnnouncement.duration) {
      addToast({ message: "Please enter Duration", status: "warning" });
      return;
    }

    const payload = {
      id: currentAnnouncement.id,
      topic: currentAnnouncement.topic,
      description: currentAnnouncement.description,
      duration: currentAnnouncement.duration,
      status: currentAnnouncement.status,
      companyIds: selectedCompanies,
      branchIds: selectedBranches,
      departmentIds: selectedDepartments,
    };

    if (isEditMode) {
      const response = await fetchApi(
        `Hrlense_Score/UpdateAnnouncement/${currentAnnouncement.id}`, "PUT", payload, null,
        "Announcement updated successfully"
      );
      if (response) {
        await loadAnnouncement();
      }
    } else {
      const response = await fetchApi<IAnnouncement>("Hrlense_Score/CreateAnnouncement", "POST", payload, null,
        "Announcement added successfully"
      );
      if (response) {
        await loadAnnouncement();
      }
    }

    setDrawerOpen(false);
  };

  const getStatusColor = (status: boolean) => (status ? "#38A169" : "#E53E3E");

  return (
    <MyDiv>
      <Box position="relative" minH="300px">
        <>
          <Flex className="page_heading flex_header">
            <Text className="font-poppins text_semibold text_3xl">Announcement</Text>
            <Flex gap={2}>
              <CustomButton title="Add New Announcement" className="btn font-poppins text_sm text_medium" rightIcon={<Image src={PlusIcon} className="logo" />} onClick={handleAddNewAnnouncement} />
            </Flex>
          </Flex>
          <Grid className="grid_container">
            <GridItem colSpan={12}>
              <CustomTables<IAnnouncement>
                value={announcement}
                loading={dataLoading}
                emptyMessage="No announcements found."
                preferenceKey="announcementTable"
                showGlobalSearch={false}
                showToolbar={true}
                lazy
                lazyParams={lazyParams}
                onLazyParamsChange={setLazyParams}
                totalRecords={totalCount}
                showPagination
                rowsPerPageOptions={[20, 100, 200, 500]}
                maxTableHeight={null}
              >
                <Column header="S. No" body={(_, index) => lazyParams.first + index + 1} />
                <Column header="Topic" field="topic" sortable filterable filterType="text" />
                <Column header="Status" field="status" filterable filterType="boolean" body={(ann: any) => (
                    <Select value={ann.status ? "true" : "false"} onChange={(e) => handleStatusChange(ann.id, e.target.value === "true")} width="120px" height="32px"
                      icon={<ChevronDownIcon />} borderRadius="4px" color={getStatusColor(ann.status)} className="font-poppins text_medium text_xsm"
                    >
                      <option value="true" style={{ color: "#38A169" }}>Active</option>
                      <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
                    </Select>
                  )}
                />
                <Column header="Description" field="description" sortable filterable filterType="text"
                  body={(rowData: any) => (
                    <Tooltip label={rowData.description} hasArrow placement="top-start">
                      <Text maxW="200px" isTruncated cursor="pointer"> {rowData.description} </Text>
                    </Tooltip>
                  )}
                />
                <Column header="Duration" field="duration" sortable />
                <Column header="Action" frozen="right"
                  body={(ann: any) => (
                    <Tooltip label="Edit Announcement" hasArrow>
                      <IconButton aria-label="Edit" icon={<EditIcon />} size="sm" colorScheme="orange" variant="ghost" onClick={() => handleEditAnnouncement(ann)} />
                    </Tooltip>
                  )}
                />
              </CustomTables>
            </GridItem>
          </Grid>

          <AlertCard isOpen={AlertOpen} onClose={() => setAlertOpen(false)} onConfirm={handleConfirmStatusChange} />

          <Drawer isOpen={DrawerOpen} placement="right" onClose={() => setDrawerOpen(false)} size="sm">
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader borderBottomWidth="1px">{isEditMode ? "Edit Announcement" : "Add New Announcement"}</DrawerHeader>
              <DrawerBody display="flex" flexDirection="column" justifyContent="space-between">
                <Box py={4}>
                  <FormControl mb={4} isRequired>
                    <FormLabel>Topic</FormLabel>
                    <Input name="topic" value={currentAnnouncement.topic} onChange={handleInputChange} placeholder="Enter Topic" />
                  </FormControl>
                  <FormControl mb={4}>
                    <FormLabel>Status</FormLabel>
                    <Select w="120px" value={currentAnnouncement.status ? "true" : "false"} onChange={(e) => handleDrawerStatusChange(e.target.value === "true")} color={getStatusColor(currentAnnouncement.status)}>
                      <option value="true" style={{ color: "#38A169" }}> Active</option>
                      <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
                    </Select>
                  </FormControl>

                  <FormControl mb={4} isRequired>
                    <FormLabel>Description</FormLabel>
                    <Input name="description" value={currentAnnouncement.description} onChange={handleInputChange} placeholder="Enter Description" />
                  </FormControl>
                  <FormControl mb={4} isRequired>
                    <FormLabel>Duration</FormLabel>
                    <Input type="number" name="duration" value={currentAnnouncement.duration} onChange={handleInputChange} placeholder="Enter Duration" />
                  </FormControl>
                </Box>

                <MultiSelectMenu isRequired label="Companies" placeholder="Select Company" options={companies} selectedValues={selectedCompanies} setSelectedValues={(values) => { setSelectedCompanies(values); loadBranches(values); }} labelKey="company_Name" valueKey="company_ID" colorScheme="blue" />

                <MultiSelectMenu isRequired label="Branches" placeholder="Select Branch" options={branchOptions} selectedValues={selectedBranches} setSelectedValues={setSelectedBranches} labelKey="branchName" valueKey="branchId" colorScheme="purple" />

                <MultiSelectMenu isRequired label="Departments" placeholder="Select Department" options={departments} selectedValues={selectedDepartments} setSelectedValues={setSelectedDepartments} labelKey="departmentName" valueKey="id" colorScheme="green" />

                <Flex justify="flex-end" mt={4} gap={2}>
                  <Button className="font-poppins text_sm text_medium" onClick={() => setDrawerOpen(false)} style={{ backgroundColor: "#D92F34", color: "white" }}>Cancel</Button>
                  <Button className="font-poppins text_sm text_medium" style={{ backgroundColor: "#006838", color: "white" }} isLoading={loading} onClick={handleSubmit} >{isEditMode ? "Update Announcement" : "Add Announcement"}</Button>
                </Flex>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        </>
      </Box>
    </MyDiv>
  );
};
export default Announcement;